import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import apiService from '../services/api';
import './CompanyNewsletterManager.css';

interface Newsletter {
  id: string;
  title: string;
  status: 'draft' | 'published' | 'sent';
  createdAt: string;
  updatedAt: string;
  previewUrl?: string;
  company: string;
  analytics?: {
    opens: number;
    clicks: number;
    bounces: number;
    delivered: number;
  };
}

interface Subscriber {
  id: string;
  email: string;
  status: 'active' | 'unsubscribed' | 'bounced';
  subscribedAt: string;
}

interface AnalyticsOverview {
  totalSent: number;
  totalOpens: number;
  totalClicks: number;
  totalBounces: number;
  openRate: number;
  clickRate: number;
  recentActivity: Array<{
    date: string;
    opens: number;
    clicks: number;
  }>;
}

const CompanyNewsletterManager: React.FC = () => {
  const { companyName } = useParams<{ companyName: string }>();
  const [newsletters, setNewsletters] = useState<Newsletter[]>([]);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [newSubscriberEmail, setNewSubscriberEmail] = useState('');
  const [activeSection, setActiveSection] = useState<'analytics' | 'subscribers' | 'newsletters'>('analytics');
  const [isSignedIn, setIsSignedIn] = useState(false);

  useEffect(() => {
    if (companyName) {
      fetchNewsletters();
      fetchAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyName]);

  // Fetch subscribers after newsletters are loaded
  useEffect(() => {
    if (newsletters.length > 0) {
      fetchSubscribers();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsletters]);

  const fetchNewsletters = async () => {
    try {
      const data = await apiService.callBackendOperation('list-newsletters');
      console.log('Raw API response:', data);

      // Transform the backend data to match our interface
      const transformedNewsletters: Newsletter[] = data.map((newsletter: any) => ({
        id: newsletter.id,
        title: newsletter.metadata?.title || `Newsletter ${newsletter.id}`,
        status: newsletter.isProd ? 'published' : 'draft',
        createdAt: newsletter.created?.toISOString() || new Date().toISOString(),
        updatedAt: newsletter.created?.toISOString() || new Date().toISOString(),
        company: companyName || '',
        previewUrl: newsletter.metadata?.previewImageUrl || newsletter.htmlUrl?.replace('/newsletter.html', '/preview.jpg'),
        analytics: newsletter.isProd ? {
          opens: Math.floor(Math.random() * 1000) + 500, // Mock analytics for now
          clicks: Math.floor(Math.random() * 200) + 50,
          bounces: Math.floor(Math.random() * 50) + 10,
          delivered: Math.floor(Math.random() * 2000) + 1000
        } : undefined
      }));

      setNewsletters(transformedNewsletters);
    } catch (err) {
      console.error('Error fetching newsletters:', err);
      setError(`Failed to fetch newsletters: ${err}`);
    }
  };

  const fetchSubscribers = async () => {
    try {
      // For now, we'll use the get-email-list endpoint with a sample newsletter ID
      // to get real subscriber data. In the future, there should be a dedicated 
      // subscriber management endpoint.
      if (newsletters.length > 0) {
        const data = await apiService.getEmailList(newsletters[0].id, false);
        console.log('Raw subscriber API response:', data);

        // Transform the backend email list to match our subscriber interface
        const transformedSubscribers: Subscriber[] = data.emails?.map((email: any, index: number) => ({
          id: (index + 1).toString(),
          email: email.email,
          status: email.status === 'SENT' ? 'active' : 'active', // Map API status to our status
          subscribedAt: email.lastUpdated || new Date().toISOString()
        })) || [];

        setSubscribers(transformedSubscribers);
      } else {
        // Fallback to mock data if no newsletters are available
        const mockSubscribers: Subscriber[] = [
          { id: '1', email: 'samip.shah@darlingmuesco.com', status: 'active', subscribedAt: '2024-01-15T10:30:00Z' },
          { id: '2', email: 'test@darlingmuesco.com', status: 'active', subscribedAt: '2024-02-20T14:15:00Z' },
        ];
        setSubscribers(mockSubscribers);
      }
    } catch (err) {
      console.error('Error fetching subscribers:', err);
      // Fallback to mock data on error
      const mockSubscribers: Subscriber[] = [
        { id: '1', email: 'samip.shah@darlingmuesco.com', status: 'active', subscribedAt: '2024-01-15T10:30:00Z' },
        { id: '2', email: 'test@darlingmuesco.com', status: 'active', subscribedAt: '2024-02-20T14:15:00Z' },
      ];
      setSubscribers(mockSubscribers);
    }
  };

  const fetchAnalytics = async () => {
    try {
      // Mock analytics data
      const mockAnalytics: AnalyticsOverview = {
        totalSent: 5540,
        totalOpens: 3350,
        totalClicks: 502,
        totalBounces: 68,
        openRate: 60.5,
        clickRate: 15.0,
        recentActivity: [
          { date: '2024-04-30', opens: 450, clicks: 65 },
          { date: '2024-04-29', opens: 380, clicks: 55 },
          { date: '2024-04-28', opens: 290, clicks: 42 },
          { date: '2024-04-27', opens: 320, clicks: 48 },
          { date: '2024-04-26', opens: 410, clicks: 62 },
        ]
      };
      setAnalytics(mockAnalytics);
    } catch (err) {
      console.error('Error fetching analytics:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNewsletter = () => {
    setShowUploadModal(true);
  };

  const handleUpload = async (formData: FormData) => {
    try {
      // Convert FormData to regular object for now
      const newsletterData = {
        title: formData.get('title'),
        company: companyName,
        // For now, we'll create a basic newsletter
        // In production, this should handle file uploads
      };
      
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const response = await apiService.createNewsletter(newsletterData);
      setShowUploadModal(false);
      fetchNewsletters(); // Refresh the list
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed');
    }
  };

  const handleDeleteNewsletter = async (newsletterId: string) => {
    const newsletter = newsletters.find(n => n.id === newsletterId);
    if (!newsletter) return;
    
    if (newsletter.status === 'published') {
      alert('Published newsletters cannot be deleted');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this draft newsletter? This will free up space in S3.')) {
      try {
        await apiService.callBackendOperation('delete-newsletter', {
          newsletterId,
          isProd: newsletter.status === 'sent'
        });
        
        // Refresh the newsletters list
        await fetchNewsletters();
        alert('Newsletter deleted successfully');
      } catch (error) {
        console.error('Delete error:', error);
        alert('Failed to delete newsletter');
      }
    }
  };

  const handleAddSubscriber = async () => {
    if (!newSubscriberEmail || !newSubscriberEmail.includes('@')) {
      alert('Please enter a valid email address');
      return;
    }
    
    // Check if email already exists
    if (subscribers.some(s => s.email === newSubscriberEmail)) {
      alert('This email is already subscribed');
      return;
    }
    
    try {
      const newSubscriber: Subscriber = {
        id: Date.now().toString(),
        email: newSubscriberEmail,
        status: 'active',
        subscribedAt: new Date().toISOString()
      };
      
      setSubscribers([...subscribers, newSubscriber]);
      setNewSubscriberEmail('');
    } catch (error) {
      console.error('Add subscriber error:', error);
      alert('Failed to add subscriber');
    }
  };

  const handleDeleteSubscriber = async (subscriberId: string) => {
    if (window.confirm('Are you sure you want to remove this subscriber?')) {
      try {
        setSubscribers(subscribers.filter(s => s.id !== subscriberId));
      } catch (error) {
        console.error('Delete subscriber error:', error);
        alert('Failed to delete subscriber');
      }
    }
  };

  const handlePreviewNewsletter = (newsletterId: string, title: string) => {
    // Open preview with example email address
    const exampleEmail = 'example@company.com';
    const previewUrl = `https://dmplnewsletter.s3.ap-south-1.amazonaws.com/email_templates/newsletter/${newsletterId}/newsletter.html?email=${exampleEmail}`;
    window.open(previewUrl, '_blank');
  };

  const handleNewsletterAnalytics = (newsletterId: string) => {
    // TODO: Open analytics modal or page for specific newsletter
    alert(`Analytics for newsletter ${newsletterId} will be shown here`);
  };

  const handleSignIn = () => {
    // TODO: Implement actual sign in
    setIsSignedIn(!isSignedIn);
  };

  const handleSendNewsletter = async (newsletterId: string, sendType: 'test' | 'prod') => {
    try {
      // For now, use existing send method
      await apiService.sendNewsletter(newsletterId, []);
      alert(`Newsletter sent (${sendType} mode)`);
      fetchNewsletters(); // Refresh the list
    } catch (error) {
      console.error('Send error:', error);
      alert('Failed to send newsletter');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading">Loading {companyName} newsletter data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error">{error}</div>
        <button onClick={() => {
          fetchNewsletters();
          fetchSubscribers();
          fetchAnalytics();
        }}>Retry</button>
      </div>
    );
  }

  return (
    <div className="company-newsletter-manager">
      {/* Header with Sign In */}
      <div className="header">
        <div className="header-left">
          <h1>{companyName?.toUpperCase()} Newsletter Manager</h1>
        </div>
        <div className="header-right">
          <button 
            className={`sign-in-btn ${isSignedIn ? 'signed-in' : ''}`}
            onClick={handleSignIn}
          >
            {isSignedIn ? '👤 Sign Out' : '🔑 Sign In'}
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="nav-tabs">
        <button 
          className={`nav-tab ${activeSection === 'analytics' ? 'active' : ''}`}
          onClick={() => setActiveSection('analytics')}
        >
          📊 Analytics
        </button>
        <button 
          className={`nav-tab ${activeSection === 'subscribers' ? 'active' : ''}`}
          onClick={() => setActiveSection('subscribers')}
        >
          👥 Subscribers ({subscribers.filter(s => s.status === 'active').length})
        </button>
        <button 
          className={`nav-tab ${activeSection === 'newsletters' ? 'active' : ''}`}
          onClick={() => setActiveSection('newsletters')}
        >
          📧 Newsletters ({newsletters.length})
        </button>
      </div>

      {/* Content based on active section */}
      <div className="content-container">
        {/* Analytics Section */}
        {activeSection === 'analytics' && analytics && (
          <div className="analytics-section">
            <h2>Newsletter Analytics Overview</h2>
            
            <div className="analytics-cards">
              <div className="analytics-card">
                <h3>📤 Total Sent</h3>
                <div className="metric">{analytics.totalSent.toLocaleString()}</div>
              </div>
              <div className="analytics-card">
                <h3>👁️ Total Opens</h3>
                <div className="metric">{analytics.totalOpens.toLocaleString()}</div>
                <div className="sub-metric">{analytics.openRate}% open rate</div>
              </div>
              <div className="analytics-card">
                <h3>🖱️ Total Clicks</h3>
                <div className="metric">{analytics.totalClicks.toLocaleString()}</div>
                <div className="sub-metric">{analytics.clickRate}% click rate</div>
              </div>
              <div className="analytics-card">
                <h3>🚫 Total Bounces</h3>
                <div className="metric">{analytics.totalBounces}</div>
              </div>
            </div>

            <div className="recent-activity">
              <h3>Recent Activity (Last 5 Days)</h3>
              <div className="activity-chart">
                {analytics.recentActivity.map((day, index) => (
                  <div key={index} className="activity-day">
                    <div className="date">{new Date(day.date).toLocaleDateString()}</div>
                    <div className="opens">Opens: {day.opens}</div>
                    <div className="clicks">Clicks: {day.clicks}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Subscribers Section */}
        {activeSection === 'subscribers' && (
          <div className="subscribers-section">
            <h2>Manage Subscribers</h2>
            
            <div className="add-subscriber">
              <input
                type="email"
                placeholder="Enter email address"
                value={newSubscriberEmail}
                onChange={(e) => setNewSubscriberEmail(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddSubscriber()}
              />
              <button onClick={handleAddSubscriber} className="add-btn">
                ➕ Add
              </button>
            </div>

            <div className="subscribers-stats">
              <span>Active: {subscribers.filter(s => s.status === 'active').length}</span>
              <span>Unsubscribed: {subscribers.filter(s => s.status === 'unsubscribed').length}</span>
              <span>Bounced: {subscribers.filter(s => s.status === 'bounced').length}</span>
            </div>

            <div className="subscribers-list">
              {subscribers.map((subscriber) => (
                <div key={subscriber.id} className={`subscriber-item ${subscriber.status}`}>
                  <div className="subscriber-email">{subscriber.email}</div>
                  <div className="subscriber-info">
                    <span className={`status-badge ${subscriber.status}`}>
                      {subscriber.status}
                    </span>
                    <span className="subscribed-date">
                      {new Date(subscriber.subscribedAt).toLocaleDateString()}
                    </span>
                  </div>
                  <button 
                    className="delete-subscriber-btn"
                    onClick={() => handleDeleteSubscriber(subscriber.id)}
                    title="Remove subscriber"
                  >
                    🗑️
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Newsletters Section */}
        {activeSection === 'newsletters' && (
          <div className="newsletters-section">
            <div className="newsletters-header">
              <h2>Newsletters</h2>
              <button 
                className="create-btn"
                onClick={handleCreateNewsletter}
              >
                ➕ Create New Newsletter
              </button>
            </div>

            <div className="newsletters-grid">
              {newsletters.length === 0 ? (
                <div className="empty-state">
                  <p>No newsletters found for {companyName}</p>
                  <button 
                    className="create-btn"
                    onClick={handleCreateNewsletter}
                  >
                    Create Your First Newsletter
                  </button>
                </div>
              ) : (
                newsletters.map((newsletter) => (
                  <div key={newsletter.id} className="newsletter-card">
                    <div className="newsletter-header">
                      <h3>{newsletter.title}</h3>
                      <span className={`status ${newsletter.status}`}>
                        {newsletter.status}
                      </span>
                    </div>
                    
                    {newsletter.previewUrl && (
                      <div className="preview-image">
                        <img src={newsletter.previewUrl} alt={newsletter.title} />
                      </div>
                    )}
                    
                    <div className="newsletter-info">
                      <p>Created: {new Date(newsletter.createdAt).toLocaleDateString()}</p>
                      <p>Updated: {new Date(newsletter.updatedAt).toLocaleDateString()}</p>
                      {newsletter.analytics && (
                        <div className="newsletter-stats">
                          <span>📤 {newsletter.analytics.delivered}</span>
                          <span>👁️ {newsletter.analytics.opens}</span>
                          <span>🖱️ {newsletter.analytics.clicks}</span>
                        </div>
                      )}
                    </div>
                    
                    <div className="newsletter-actions">
                      <button 
                        onClick={() => handlePreviewNewsletter(newsletter.id, newsletter.title)}
                        title="Preview with example email"
                      >
                        👁️ Preview
                      </button>
                      
                      {newsletter.status === 'published' && newsletter.analytics && (
                        <button 
                          onClick={() => handleNewsletterAnalytics(newsletter.id)}
                          title="View analytics"
                        >
                          📊 Analytics
                        </button>
                      )}
                      
                      {newsletter.status === 'draft' && (
                        <>
                          <button onClick={() => handleSendNewsletter(newsletter.id, 'test')}>
                            🧪 Test Send
                          </button>
                          <button 
                            className="delete-btn"
                            onClick={() => handleDeleteNewsletter(newsletter.id)}
                            title="Delete draft and free S3 space"
                          >
                            🗑️ Delete
                          </button>
                        </>
                      )}
                      
                      {newsletter.status === 'published' && (
                        <button 
                          onClick={() => handleSendNewsletter(newsletter.id, 'prod')}
                          disabled
                          title="Published newsletters cannot be sent again"
                        >
                          ✅ Published
                        </button>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {/* Upload Modal */}
      {showUploadModal && (
        <UploadModal
          companyName={companyName || ''}
          onUpload={handleUpload}
          onClose={() => setShowUploadModal(false)}
        />
      )}
    </div>
  );
};

// Upload Modal Component
interface UploadModalProps {
  companyName: string;
  onUpload: (formData: FormData) => void;
  onClose: () => void;
}

const UploadModal: React.FC<UploadModalProps> = ({ companyName, onUpload, onClose }) => {
  const [title, setTitle] = useState('');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [previewImage, setPreviewImage] = useState<File | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title || !pdfFile || !previewImage) {
      alert('Please fill in all required fields');
      return;
    }

    const formData = new FormData();
    formData.append('title', title);
    formData.append('pdf', pdfFile);
    formData.append('preview', previewImage);
    formData.append('company', companyName);

    onUpload(formData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>Upload Newsletter for {companyName}</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="upload-form">
          <div className="form-group">
            <label htmlFor="title">Newsletter Title *</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="pdf">PDF Newsletter File *</label>
            <input
              type="file"
              id="pdf"
              accept=".pdf"
              onChange={(e) => setPdfFile(e.target.files?.[0] || null)}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="preview">Preview Image *</label>
            <input
              type="file"
              id="preview"
              accept="image/*"
              onChange={(e) => setPreviewImage(e.target.files?.[0] || null)}
              required
            />
          </div>
          
          <div className="form-actions">
            <button type="button" onClick={onClose}>Cancel</button>
            <button type="submit">Upload Newsletter</button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CompanyNewsletterManager;